import APIManagerRemoteApi, { APIPolicyBody } from '../../../../remote-api/APIManagerRemoteApi'
import { LogPointsRuntimeHelper, RuntimeMajor, RuntimeSupport } from './LogPointsRuntimeHelper'
import {
  APIDetail,
  APIPolicy,
  APIPolicyConfiguration,
  ExchangeTemplatePolicy,
  isSplitMessageLoggingPolicyConfiguration,
} from '../../../../remote-api/models'
import { addDataWeaveExpressions } from './MessageLoggingDWExpressions'
import { getFeatureFlag } from '../../../../util/flags'
import { isFlex } from '../../../../util/flex'

export class LogPointsMuleFourHelper extends LogPointsRuntimeHelper {

  private static readonly POLICY_VERSION_ID = getFeatureFlag('logPoints.apis.muleFourMessageLoggingVersion') || '1.0.0'

  private readonly remoteAPI: APIManagerRemoteApi

  constructor(remoteAPI: APIManagerRemoteApi) {
    super(RuntimeMajor.MULE_FOUR)

    this.remoteAPI = remoteAPI
  }

  async fetchMessageLoggingPolicy(orgId: string, envId: string, apiId: string): Promise<APIPolicy | undefined> {
    const policies = await this.remoteAPI.fetchAPIPolicies(orgId, envId, apiId)
    const policyFinder = (policy: APIPolicy) => policy.assetId === LogPointsRuntimeHelper.POLICY_ID

    return policies.find(policyFinder)
  }

  async getAPIPolicyBody(api: APIDetail, configuration: APIPolicyConfiguration, policy?: APIPolicy): Promise<APIPolicyBody> {
    const mappedConfigs = addDataWeaveExpressions(configuration, this.runtime)

    const {
      id,
      groupId,
      assetId,
      assetVersion,
      policyTemplateId
    } = LogPointsMuleFourHelper.getExtraAPIPolicyBodyValues(policy)

    const configurationData = LogPointsMuleFourHelper.getConfigurationData(mappedConfigs, policy)

    return {
      apiId: Number(api.id),
      apiVersionId: Number(api.id),
      configurationData: configurationData,
      pointcutData: null,
      id,
      groupId,
      assetId,
      assetVersion,
      policyTemplateId
    }
  }

  canHandle(api: APIDetail): boolean {
    const endpoint = api.endpoint
    const muleVersion40OrAbove = endpoint && endpoint.muleVersion4OrAbove

    return !isFlex(api) || !!muleVersion40OrAbove
  }

  protected async fetchMinMuleVersion(orgId: string): Promise<string | undefined> {
    const messageLoggingTemplate = await this.getMessageLoggingTemplate(orgId)

    return messageLoggingTemplate && messageLoggingTemplate.minMuleVersion
  }

  protected getDefaultRuntimeSupport(): RuntimeSupport {
    return {minMuleVersion: {major: 4, minor: 1, patch: 1}}
  }

  private async getMessageLoggingTemplate(orgId: string): Promise<ExchangeTemplatePolicy | undefined> {
    const exchangePolicyTemplate = await this.remoteAPI.fetchExchangePolicyTemplates(orgId)

    return exchangePolicyTemplate.find(template => {
      const matchesPolicyId = template.assetId === LogPointsRuntimeHelper.POLICY_ID
      const matchesPolicyVersion = template.version === LogPointsMuleFourHelper.POLICY_VERSION_ID

      return matchesPolicyId && matchesPolicyVersion
    })
  }

  private static getConfigurationData(mappedConfigs: APIPolicyConfiguration, policy?: APIPolicy) {
    if (policy) {
      return !isSplitMessageLoggingPolicyConfiguration(policy.configurationData) ? mappedConfigs : {loggingConfiguration: mappedConfigs}
    } else if (this.POLICY_VERSION_ID === getFeatureFlag('logPoints.apis.splitMessageLoggingVersion')) {
      return {loggingConfiguration: mappedConfigs}
    } else {
      return mappedConfigs
    }
  }

  private static getExtraAPIPolicyBodyValues(policy?: APIPolicy) {
    const id = policy ? policy.id : null
    let groupId = policy && policy.groupId
    let assetId = policy && policy.assetId
    let assetVersion = policy && policy.assetVersion
    let policyTemplateId = policy && policy.policyTemplateId ? policy.policyTemplateId : undefined

    if (!policy) {
      groupId = LogPointsRuntimeHelper.GROUP_ID
      assetId = LogPointsRuntimeHelper.POLICY_ID
      assetVersion = LogPointsMuleFourHelper.POLICY_VERSION_ID
    }

    return {id, groupId, assetId, assetVersion, policyTemplateId}
  }
}
